import { Link, useNavigate } from '@tanstack/react-router';
import { Menu, Search } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Dialog, ThemeToggle } from '@/components/ui';
import { useSession } from '@/hooks/use-session';

import { BrandLogo } from './brand-logo';
import { NotificationsBell } from './notifications-bell';
import { UserMenu } from './user-menu';
import { WorkspaceNav } from './workspace-nav';

/**
 * Каркас робочої зони (макет Paper «Dashboard — A3»): повноширинна смуга
 * згори, бічна панель ліворуч, вміст праворуч. Маркетингової шапки й підвала
 * тут немає — дилер уже всередині продукту, а не на вітрині.
 *
 * На вузькому екрані панель ховається в шухляду. Саме шухляда, а не схований
 * блок у розмітці: Radix монтує вміст лише відкритим, тож у DOM ніколи не
 * буває другого дзвіночка чи другої навігації. Наскрізна перевірка
 * `notifications.mjs` заходить сюди й шукає дзвіночок за роллю — дубль у
 * дереві зламав би її, і саме через такий дубль колись довелось дописувати
 * `.first()` у перевірках шапки.
 */
export function WorkspaceShell({ children }: { children: ReactNode }): ReactNode {
	const [navOpen, setNavOpen] = useState(false);
	const { user } = useSession();

	return (
		<>
			<header className="sticky top-0 z-40 flex h-header shrink-0 items-center gap-3 border-b border-border bg-surface px-4 lg:px-6">
				<button
					type="button"
					onClick={() => setNavOpen(true)}
					className="grid size-10 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-surface-muted lg:hidden"
					aria-label="Open menu"
				>
					<Menu className="size-5" />
				</button>

				<Link to="/dashboard" aria-label="Sirius Hub — home" className="shrink-0">
					<BrandLogo compact />
				</Link>

				<WorkspaceSearch />

				<div className="ms-auto flex shrink-0 items-center gap-1">
					<ThemeToggle className="hidden md:flex" />
					<NotificationsBell />
					{user && <UserMenu user={user} showName />}
				</div>
			</header>

			<div className="flex flex-1">
				<aside className="sticky top-header hidden h-[calc(100dvh-var(--spacing-header))] w-56 shrink-0 overflow-y-auto border-r border-border bg-surface px-4 pb-6 pt-7 lg:block">
					<WorkspaceNav />
				</aside>

				<main className="min-w-0 flex-1">{children}</main>
			</div>

			<Dialog
				open={navOpen}
				onOpenChange={setNavOpen}
				variant="drawer"
				title="Menu"
				description="Sections of your Sirius Hub account"
			>
				<div className="flex-1 overflow-y-auto p-4">
					<WorkspaceNav onNavigate={() => setNavOpen(false)} />
				</div>
			</Dialog>
		</>
	);
}

/**
 * Пошук у смузі — це ЯРЛИК у каталог, а не другий пошуковий рушій.
 *
 * Каталог уже шукає рівно це (`?q=` у `catalogueSearchSchema`, фільтрує
 * провайдер), тож поле лише переносить запит туди. Випадних підказок тут
 * свідомо немає: поле, яке обіцяє більше, ніж робить, гірше за просте.
 */
function WorkspaceSearch(): ReactNode {
	const navigate = useNavigate();
	const [value, setValue] = useState('');

	return (
		<form
			className="hidden min-w-0 flex-1 justify-center md:flex"
			onSubmit={(event) => {
				event.preventDefault();
				void navigate({ to: '/auctions', search: { q: value.trim() || undefined } });
			}}
		>
			<label className="relative w-full max-w-107">
				<Search aria-hidden="true" className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-fg-muted" />
				<input
					type="search"
					value={value}
					onChange={(event) => setValue(event.target.value)}
					placeholder="Search VIN, lot number or vehicle"
					aria-label="Search vehicles"
					className="h-10.5 w-full rounded-lg border border-border-strong bg-surface pl-10 pr-3.5 text-sm text-fg placeholder:text-fg-muted focus:border-primary focus:outline-none"
				/>
			</label>
		</form>
	);
}
